/**
 * Fallback look-ahead horizon when the project config request fails.
 * A successful config replaces these counts.
 */
export const TARGETS_LOOKAHEAD = {
  /** Calendar days from today */
  dayCount: 21,
  /** ISO weeks from the current week */
  weekCount: 3,
  /** Calendar months from the current month */
  monthCount: 3,
  /** Export end-date cap, in days from today */
  maxDaysFromToday: 45,
} as const;

export type LookAheadPeriod = 'day' | 'week' | 'month';

export interface TargetsLookAheadConfig {
  dayCount: number | null;
  weekCount: number | null;
  monthCount: number | null;
  maxDaysFromToday: number;
}

export function fallbackLookAheadConfig(): TargetsLookAheadConfig {
  return {
    dayCount: TARGETS_LOOKAHEAD.dayCount,
    weekCount: TARGETS_LOOKAHEAD.weekCount,
    monthCount: TARGETS_LOOKAHEAD.monthCount,
    maxDaysFromToday: TARGETS_LOOKAHEAD.maxDaysFromToday,
  };
}

export interface LookAheadPeriodChoice {
  value: LookAheadPeriod;
  label: string;
  count: number;
}

function periodLabel(count: number, unit: string): string {
  return `${count} ${unit}${count === 1 ? '' : 's'}`;
}

/** Dropdown options for positive counts only. maxDaysFromToday is not an option. */
export function lookAheadPeriodChoices(config: TargetsLookAheadConfig): LookAheadPeriodChoice[] {
  const choices: LookAheadPeriodChoice[] = [];
  if (config.dayCount != null && config.dayCount > 0) {
    choices.push({ value: 'day', label: periodLabel(config.dayCount, 'day'), count: config.dayCount });
  }
  if (config.weekCount != null && config.weekCount > 0) {
    choices.push({ value: 'week', label: periodLabel(config.weekCount, 'week'), count: config.weekCount });
  }
  if (config.monthCount != null && config.monthCount > 0) {
    choices.push({ value: 'month', label: periodLabel(config.monthCount, 'month'), count: config.monthCount });
  }
  return choices;
}

export function countForLookAheadPeriod(config: TargetsLookAheadConfig, period: LookAheadPeriod): number {
  const raw =
    period === 'day' ? config.dayCount : period === 'week' ? config.weekCount : config.monthCount;
  if (raw != null && raw > 0) return raw;
  if (period === 'day') return TARGETS_LOOKAHEAD.dayCount;
  if (period === 'week') return TARGETS_LOOKAHEAD.weekCount;
  return TARGETS_LOOKAHEAD.monthCount;
}
